import React, { useState, useEffect, useRef, useCallback } from 'react';
import Icon from './ui/Icon';
import { toast } from './Toast';
import {
  getFazendas,
  getFazendaAtiva,
  trocarFazenda,
  ouvirFazenda,
} from '../sessao';

/**
 * Seletor de fazenda ativa. Mostra a fazenda atual + o papel do usuário nela e,
 * ao abrir, lista todas as fazendas do usuário para trocar sem novo login.
 * Se o usuário só participa de uma fazenda, exibe apenas o nome (sem menu).
 */
export default function SeletorFazenda({ variante = 'sidebar' }) {
  const [aberto, setAberto] = useState(false);
  const [fazendas, setFazendas] = useState(getFazendas());
  const [ativa, setAtiva] = useState(getFazendaAtiva());
  const [trocando, setTrocando] = useState(false);
  const ref = useRef(null);

  useEffect(() => ouvirFazenda(() => {
    setFazendas(getFazendas());
    setAtiva(getFazendaAtiva());
  }), []);

  // Fecha ao clicar fora.
  useEffect(() => {
    if (!aberto) return undefined;
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setAberto(false); };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [aberto]);

  const selecionar = useCallback(async (f) => {
    if (f.vinculoStatus !== 'ATIVO') return;
    if (ativa && String(f.fazendaId) === String(ativa.fazendaId)) { setAberto(false); return; }
    setTrocando(true);
    try {
      await trocarFazenda(f.fazendaId);
      toast(`Agora você está em ${f.fazendaNome}.`, 'success');
      setAberto(false);
      // Recarrega para todas as telas reescoparem os dados na nova fazenda.
      window.location.assign('/');
    } catch (e) {
      toast(e.message || 'Não foi possível trocar de fazenda.', 'error');
    } finally {
      setTrocando(false);
    }
  }, [ativa]);

  if (!ativa) return null;

  const soUma = fazendas.length <= 1;

  // Só uma fazenda: card rotulado (sem menu). O rótulo "Fazenda" deixa claro
  // que aquela informação é a fazenda atual, não repetição do nome do app.
  if (soUma) {
    return (
      <div className={`seletor-fazenda seletor-fazenda--${variante} seletor-fazenda--fixa`}>
        <span className="seletor-fazenda__prefixo">Fazenda ativa:</span>{' '}
        <span className="seletor-fazenda__nome">{ativa.fazendaNome}</span>
      </div>
    );
  }

  return (
    <div className={`seletor-fazenda seletor-fazenda--${variante}`} ref={ref}>
      <button
        type="button"
        className="seletor-fazenda__atual"
        aria-haspopup="listbox"
        aria-expanded={aberto}
        aria-label={`Fazenda atual: ${ativa.fazendaNome}. Toque para trocar de fazenda.`}
        onClick={() => setAberto((v) => !v)}
        disabled={trocando}
      >
        <span className="seletor-fazenda__texto">
          <span className="seletor-fazenda__prefixo">Fazenda ativa:</span>{' '}
          <span className="seletor-fazenda__nome">{ativa.fazendaNome}</span>
        </span>
        <Icon nome={aberto ? 'chevron-cima' : 'chevron-baixo'} tamanho={14} />
      </button>

      {aberto && (
        <div className="seletor-fazenda__menu" role="listbox">
          {fazendas.map((f) => {
            const isAtiva = String(f.fazendaId) === String(ativa.fazendaId);
            const pendente = f.vinculoStatus === 'PENDENTE';
            const inativa = f.vinculoStatus === 'INATIVO';
            const indisponivel = pendente || inativa;
            return (
              <button
                key={f.fazendaId}
                type="button"
                role="option"
                aria-selected={isAtiva}
                className={`seletor-fazenda__item ${isAtiva ? 'seletor-fazenda__item--ativa' : ''} ${indisponivel ? 'seletor-fazenda__item--indisponivel' : ''}`}
                onClick={() => selecionar(f)}
                disabled={indisponivel || trocando}
              >
                <span className="seletor-fazenda__item-info">
                  <span className="seletor-fazenda__item-nome">{f.fazendaNome}</span>
                  <span className="seletor-fazenda__item-sub">
                    {pendente ? 'Aguardando aprovação'
                      : inativa ? 'Sem acesso'
                      : (f.role === 'ADMIN' ? 'Admin' : 'Membro')}
                    {f.assinaturaVencida && !indisponivel ? ' · Assinatura vencida' : ''}
                  </span>
                </span>
                {isAtiva && <Icon nome="ok" tamanho={16} />}
                {f.assinaturaVencida && !indisponivel && !isAtiva && (
                  <span className="seletor-fazenda__aviso"><Icon nome="alerta" tamanho={14} /></span>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
